import React from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';

const ContactForm = ({
  t,
  isRTL,
  formData,
  isSubmitted,
  isLoading,
  errorMessage,
  onChange,
  onSubmit,
}) => {
  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-green-50 border border-green-200 rounded-xl p-8 text-center"
        data-testid="contact-form-success"
      >
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-green-900 mb-2">{t.contact.form.success}</h3>
        <p className="text-gray-700">{t.contact.form.successMessage}</p>
      </motion.div>
    );
  }

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9B4B8C] focus:border-transparent transition-all';

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
      {errorMessage && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
          data-testid="contact-form-error"
        >
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.contact.form.company}
          </label>
          <input
            type="text"
            name="company"
            required
            value={formData.company}
            onChange={onChange}
            className={inputClass}
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="contact-input-company"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.contact.form.name}
          </label>
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={onChange}
            className={inputClass}
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="contact-input-name"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.contact.form.email}
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={onChange}
            className={inputClass}
            dir="ltr"
            data-testid="contact-input-email"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.contact.form.phone}
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={onChange}
            className={inputClass}
            dir="ltr"
            data-testid="contact-input-phone"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t.contact.form.subject}
        </label>
        <select
          name="subject"
          required
          value={formData.subject}
          onChange={onChange}
          className={`${inputClass} bg-white`}
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="contact-select-subject"
        >
          {t.contact.subjects.map((subject) => (
            <option key={subject.value} value={subject.value}>
              {subject.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t.contact.form.message}
        </label>
        <textarea
          name="message"
          required
          rows={5}
          value={formData.message}
          onChange={onChange}
          className={`${inputClass} resize-none`}
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="contact-textarea-message"
        />
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full bg-[#9B4B8C] text-white py-4 rounded-lg font-medium hover:bg-[#7d3c71] transition-colors flex items-center justify-center gap-2 disabled:opacity-70 ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
        data-testid="contact-submit-button"
      >
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            {t.contact.form.sending}
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {t.contact.form.submit}
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
